/**
 * ModulesServiceModule — 2026-06-07 wire-up for the seed/modules/ REST surface.
 *
 * Wraps the briefing-room backend's modules library endpoints. Lets the SPA
 * list installed adventure modules and load a single module's full JSON
 * shape (NPCs, locations, events, mission sheets, cross-references).
 *
 * Routes (per briefing_room_backend/routes/modules.py):
 *   GET /api/modules/briefing-room/modules              → ModulesManifest
 *   GET /api/modules/briefing-room/modules/{module_id}  → full module JSON | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders() (mirrors
 * HeroPointServiceModule.ts and PersistenceServiceModule.ts patterns).
 */
import { getAuthHeaders } from './authHeaders';

/** Per-module summary returned by the manifest endpoint. */
export interface ModuleManifestEntry {
  module_id: string;
  display_name: string;
  dialect: 'victory_games_canon' | 'fan_authored_4eyes';
  publisher: 'victory_games' | '4eyes' | 'internal' | 'other';
  sku: string | null;
  year: number;
  size_bytes: number;
}

export interface ModulesManifest {
  modules: ModuleManifestEntry[];
  total: number;
}

const BASE_PATH = '/api/modules/briefing-room/modules';

export class ModulesServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  /**
   * List all installed adventure modules. Returns an empty array (not
   * null/undefined) when seed/modules/ is empty.
   */
  async list(): Promise<ModulesManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`ModulesServiceModule: list HTTP ${resp.status}`);
    }
    return (await resp.json()) as ModulesManifest;
  }

  /**
   * Fetch a single module's full JSON. Returns null on 404 so callers
   * can branch on missing-vs-error.
   */
  async get(moduleId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(moduleId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(`ModulesServiceModule: get HTTP ${resp.status}`);
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
